import { Fragment, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material'
import MainLayout from '../../layouts/MainLayout'
import PageHeader from '../../components/PageHeader'
import baseService from '../../services/baseService'

const EMPTY_FILTERS = {
  from: '', to: '', application_id: '', organization_id: '', user_id: '',
  model_type: '', action: '', data: '', state: '', city: '',
}
const ACTIONS = [
  ['create', 'CREATE'], ['update', 'UPDATE'], ['delete', 'DELETE'],
  ['restore', 'RESTORE'], ['force_delete', 'FORCE DELETE'],
]
const FIELD_LABELS = {
  id: 'ID', name: 'Nome', description: 'Descrição', details: 'Detalhes',
  status_id: 'Status', condition_id: 'Condição', type_id: 'Tipo',
  organization_id: 'Organização', current_organization_id: 'Organização atual',
  supplier_id: 'Fornecedor', customer_id: 'Cliente', contract_id: 'Contrato',
  equipment_id: 'Equipamento', product_id: 'Produto', category_id: 'Categoria',
  address_id: 'Endereço', location_id: 'Localização', user_id: 'Usuário',
  created_by: 'Criado por', updated_by: 'Atualizado por', confirmed_by: 'Confirmado por',
  number: 'Número', serial_number: 'Número de série', quantity: 'Quantidade',
  price: 'Valor', value: 'Valor', delivery_date: 'Data da entrega',
  movement_date: 'Data da movimentação', start_date: 'Data inicial', end_date: 'Data final',
  deleted_at: 'Data de exclusão', active: 'Ativo', email: 'E-mail',
}
const TABLE_LABELS = {
  addresses: 'Endereços', address: 'Endereços', applications: 'Aplicações', application: 'Aplicações',
  audit_logs: 'Logs de auditoria', brands: 'Marcas', brand: 'Marcas', categories: 'Categorias', category: 'Categorias',
  conditions: 'Condições', condition: 'Condições', contacts: 'Contatos', contact: 'Contatos',
  contracts: 'Contratos', contract: 'Contratos', contract_events: 'Eventos de contrato', contract_event: 'Eventos de contrato',
  contract_event_types: 'Tipos de evento de contrato', contract_event_type: 'Tipos de evento de contrato',
  contract_infos: 'Informações de contrato', contract_info: 'Informações de contrato',
  contract_items: 'Itens de contrato', contract_item: 'Itens de contrato', contract_jobs: 'Funções de contrato', contract_job: 'Funções de contrato',
  contract_statuses: 'Situações de contrato', contract_status: 'Situações de contrato', contract_types: 'Tipos de contrato', contract_type: 'Tipos de contrato',
  customers: 'Clientes', customer: 'Clientes', document_types: 'Tipos de documento', document_type: 'Tipos de documento',
  destination_orders: 'Destinos de ordem', destination_order: 'Destinos de ordem', equipments: 'Equipamentos', equipment: 'Equipamentos',
  return_confirmations: 'Confirmações de devolução', return_confirmations_items: 'Itens de confirmação de devolução',
  equipment_histories: 'Histórico de equipamentos', equipment_history: 'Histórico de equipamentos',
  equipment_kits: 'Kits de equipamentos', equipment_kit: 'Kits de equipamentos', equipment_kit_items: 'Itens de kits de equipamentos', equipment_kit_item: 'Itens de kits de equipamentos',
  event_additives: 'Aditivos de evento', event_additive: 'Aditivos de evento', groups: 'Grupos', group: 'Grupos', locations: 'Localizações', location: 'Localizações',
  movements: 'Movimentações', movement: 'Movimentações', movement_items: 'Itens de movimentação', movement_item: 'Itens de movimentação',
  movement_status: 'Status de movimentação', movement_statuses: 'Status de movimentação', movement_type: 'Tipos de movimentação', movement_types: 'Tipos de movimentação',
  organizations: 'Organizações', organization: 'Organizações', permissions: 'Permissões', permission: 'Permissões', products: 'Produtos', product: 'Produtos',
  product_items: 'Itens de produtos', product_item: 'Itens de produtos', roles: 'Cargos', role: 'Cargos', service_orders: 'Ordens de serviço', service_order: 'Ordens de serviço',
  service_order_accessories: 'Acessórios de ordem de serviço', service_order_accessory: 'Acessórios de ordem de serviço',
  service_order_departments: 'Departamentos de ordem de serviço', service_order_department: 'Departamentos de ordem de serviço',
  service_order_items: 'Itens de ordem de serviço', service_order_item: 'Itens de ordem de serviço',
  service_order_mode_transport: 'Modos de transporte de ordem de serviço', service_order_mode_transports: 'Modos de transporte de ordem de serviço',
  service_order_status: 'Status de ordem de serviço', service_order_statuses: 'Status de ordem de serviço',
  shipments: 'Remessas', shipment: 'Remessas', shipment_items: 'Itens de remessa', shipment_item: 'Itens de remessa',
  shipment_status: 'Status de remessa', shipment_statuses: 'Status de remessa', statuses: 'Status', status: 'Status',
  subgroups: 'Subgrupos', subgroup: 'Subgrupos', suppliers: 'Fornecedores', supplier: 'Fornecedores',
  technician_status: 'Status de técnico', technician_statuses: 'Status de técnico', transactions: 'Transações', transaction: 'Transações',
  transaction_items: 'Itens de transação', transaction_item: 'Itens de transação', transaction_types: 'Tipos de transação', transaction_type: 'Tipos de transação',
  types: 'Tipos', type: 'Tipos', units: 'Unidades', unit: 'Unidades', volumes: 'Volumes', volume: 'Volumes',
  volume_items: 'Itens de volume', volume_item: 'Itens de volume', billings: 'Faturamentos', billing: 'Faturamentos',
  billing_items: 'Itens de faturamento', billing_item: 'Itens de faturamento',
}
const asObject = (value) => {
  if (value == null) return {}
  if (typeof value === 'object') return value
  try { return JSON.parse(value) || {} } catch { return {} }
}
const beforeSnapshot = (log) => asObject(log.before)
const beforeLabelSnapshot = (log) => asObject(log.before_labels)
const afterSnapshot = (log) => {
  const after = asObject(log.after)
  // A versão anterior do logger gravava no UPDATE apenas os campos enviados.
  // Completa esses logs com os campos anteriores, já que a ausência significa
  // "não alterado"; um campo removido deve estar presente com valor null.
  return log.action === 'update' ? { ...beforeSnapshot(log), ...after } : after
}
const afterLabelSnapshot = (log) => {
  const before = beforeSnapshot(log)
  const after = asObject(log.after)
  const beforeLabels = beforeLabelSnapshot(log)
  const newLabels = asObject(log.after_labels)
  const labels = log.action === 'update' ? { ...beforeLabels, ...newLabels } : newLabels
  if (log.action === 'update') {
    Object.keys(before).forEach((key) => {
      if ((key.endsWith('_id') || key.endsWith('_by'))
        && JSON.stringify(before[key]) !== JSON.stringify(after[key] ?? null)
        && !Object.prototype.hasOwnProperty.call(newLabels, key)) delete labels[key]
    })
  }
  return labels
}
const fieldLabel = (field) => FIELD_LABELS[field] || field.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())
const displayFieldValue = (value, label) => value == null || value === ''
  ? '—'
  : `${readable(value)}${label ? ` — ${label}` : ''}`
const dateToQuery = (value) => {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value
}
const maskDate = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}
const escapeHtml = (value) => String(value ?? '—').replace(/[&<>"']/g, (char) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
}[char]))

function getChanges(log) {
  const before = beforeSnapshot(log)
  const after = afterSnapshot(log)
  const ignored = new Set(['created_at', 'updated_at'])
  // Em logs de versões anteriores, o snapshot "depois" do update guardava
  // somente os atributos enviados pelo model. Ausência de uma chave, portanto,
  // não significa que ela foi apagada nem que mudou.
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  const changedKeys = keys
    .filter((key) => !ignored.has(key))
    .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
  const beforeLabels = beforeLabelSnapshot(log)
  const afterLabels = afterLabelSnapshot(log)
  return changedKeys.map((key) => ({
    key,
    fieldLabel: fieldLabel(key),
    before: before[key],
    after: after[key],
    beforeLabel: beforeLabels[key],
    afterLabel: afterLabels[key],
  }))
}
const readable = (value) => value == null ? '—' : typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
const snapshotText = (value, labels = {}) => Object.entries(asObject(value))
  .map(([key, field]) => `${fieldLabel(key)} (${key}) = ${displayFieldValue(field, labels[key])}`)
  .join('\n') || 'Sem dados registrados.'
const displayTable = (log) => {
  const table = (log.model_table || log.model_type || 'registro').split('.').pop()
  const appName = log.application?.name || 'Sistema'
  const appPrefix = appName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replaceAll(' ', '_')
  let tableName = table.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
  if (tableName.startsWith(`${appPrefix}_`)) tableName = tableName.slice(appPrefix.length + 1)
  return TABLE_LABELS[tableName] || tableName.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())
}
const displayApplication = (log) => {
  const name = log.application?.name || '—'
  const localizedName = name.toUpperCase() === 'ALUGUEL' ? 'Aluguel' : name
  return name === '—' ? name : `${localizedName} - ${displayTable(log)}`
}
const displayDateTime = (value) => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : `${date.toLocaleDateString('pt-BR')} - ${date.toLocaleTimeString('pt-BR')}`
}

function printChange(log, changes) {
  const changedFields = changes.length ? changes.map(({ fieldLabel }) => fieldLabel).join(', ') : 'Nenhum campo alterado'
  const changedRows = changes.length
    ? changes.map(({ fieldLabel, before, after, beforeLabel, afterLabel }) => `<tr><th>${escapeHtml(fieldLabel)}</th><td>${escapeHtml(displayFieldValue(before, beforeLabel))}</td><td>${escapeHtml(displayFieldValue(after, afterLabel))}</td></tr>`).join('')
    : '<tr><td colspan="3">Nenhuma diferença registrada.</td></tr>'
  const modifiedSection = log.action === 'update'
    ? `<h2>Dados Modificados</h2><table><thead><tr><th>Campo</th><th>Valor antigo</th><th>Valor novo</th></tr></thead><tbody>${changedRows}</tbody></table>`
    : ''
  const changedFieldsSummary = log.action === 'update'
    ? `<br><b>Campos alterados:</b> ${escapeHtml(changedFields)}`
    : ''
  const popup = window.open('', '_blank', 'width=900,height=700')
  if (!popup) return
  popup.document.write(`<!doctype html><html><head><title>Relatório de alteração</title><style>body{font:14px Arial;margin:32px;color:#222}h1{font-size:22px}.metadata{line-height:1.7}.snapshots{display:grid;grid-template-columns:1fr 1fr;gap:24px}pre{font:13px monospace;white-space:pre-wrap;overflow-wrap:anywhere;border:1px solid #bbb;padding:14px;min-height:120px}table{border-collapse:collapse;width:100%;margin:14px 0 24px}th,td{border:1px solid #bbb;padding:8px;text-align:left;white-space:pre-wrap;overflow-wrap:anywhere}th{background:#f2f2f2}button{padding:8px 14px;margin-bottom:18px}@media print{button{display:none}.snapshots{break-inside:avoid}}</style></head><body><button onclick="window.print()">Imprimir</button><h1>Relatório de alteração</h1><div class="metadata"><b>ID:</b> ${log.id}<br><b>Data/Hora:</b> ${escapeHtml(displayDateTime(log.created_at))}<br><b>Aplicação:</b> ${escapeHtml(displayApplication(log))}<br><b>Órgão:</b> ${escapeHtml(log.organization?.name || '—')}<br><b>Usuário:</b> ${escapeHtml(log.user?.username || log.user?.name || `Usuário ${log.user_id || 'sistema'}`)}<br><b>Operação:</b> ${escapeHtml(log.action.toUpperCase())}<br><b>Dados:</b> Tabela ${escapeHtml(displayTable(log))}<br><b>Modificado em:</b> id = ${escapeHtml(log.model_id)}${changedFieldsSummary}</div>${modifiedSection}<div class="snapshots"><section><h2>Dados Antigos</h2><pre>${escapeHtml(snapshotText(log.before, beforeLabelSnapshot(log)))}</pre></section><section><h2>Dados Novos</h2><pre>${escapeHtml(snapshotText(afterSnapshot(log), afterLabelSnapshot(log)))}</pre></section></div></body></html>`)
  popup.document.close()
  popup.focus()
  popup.print()
}

export default function AuditLogsPage() {
  const [page, setPage] = useState(1)
  const [perPage] = useState(25)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [applied, setApplied] = useState(EMPTY_FILTERS)
  const [selectedLog, setSelectedLog] = useState(null)
  const [userSearch, setUserSearch] = useState('')
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('')
  const [selectedUser, setSelectedUser] = useState(null)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 300)
    return () => clearTimeout(timer)
  }, [userSearch])
  const { data: matchedUsers = [], isFetching: isSearchingUsers } = useQuery({
    queryKey: ['audit-log-users', debouncedUserSearch],
    queryFn: async () => (await baseService.getByColumn(`/audit-logs/users?search=${encodeURIComponent(debouncedUserSearch)}`)).result || [],
    enabled: debouncedUserSearch.length >= 2,
    staleTime: 30_000,
  })
  const params = useMemo(() => {
    const query = new URLSearchParams({ page, perPage })
    Object.entries(applied).forEach(([key, value]) => {
      if (value) query.set(key, ['from', 'to'].includes(key) ? dateToQuery(value) : value)
    })
    return query.toString()
  }, [page, perPage, applied])
  const { data, isLoading, isError } = useQuery({
    queryKey: ['audit-logs', page, perPage, applied],
    queryFn: async () => (await baseService.getByColumn(`/audit-logs?${params}`)).result,
    placeholderData: (previous) => previous,
  })
  const logs = data?.logs?.data || []
  const options = data?.filterOptions || {}
  const selectedChanges = selectedLog ? getChanges(selectedLog) : []

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }))
  const selectFilter = (key, title, values, valueKey = 'id', textKey = 'name', textMapper = null) => (
    <TextField select size="small" label={title} value={filters[key]} onChange={(event) => setFilter(key, event.target.value)} sx={{ minWidth: 175 }}>
      <MenuItem value="">Todos</MenuItem>
      {(values || []).map((item) => {
        const value = typeof item === 'string' ? item : item[valueKey]
        const text = typeof item === 'string' ? (textMapper ? textMapper(item) : item) : item[textKey]
        return <MenuItem key={value} value={value}>{text}</MenuItem>
      })}
    </TextField>
  )

  return <MainLayout>
    <Box sx={{ p: 3 }}>
      <PageHeader title="Logs do sistema" />
      <Paper sx={{ p: 2, my: 2 }} component="form" onSubmit={(event) => { event.preventDefault(); setPage(1); setApplied({ ...filters }) }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField size="small" label="Data inicial" placeholder="dd/mm/aaaa" value={filters.from} onChange={(event) => setFilter('from', maskDate(event.target.value))} inputProps={{ inputMode: 'numeric', maxLength: 10 }} />
          <TextField size="small" label="Data final" placeholder="dd/mm/aaaa" value={filters.to} onChange={(event) => setFilter('to', maskDate(event.target.value))} inputProps={{ inputMode: 'numeric', maxLength: 10 }} />
          {selectFilter('application_id', 'Aplicativo', options.applications)}
          {selectFilter('organization_id', 'Órgão / organização', options.organizations)}
          <Autocomplete
            size="small"
            options={matchedUsers}
            value={selectedUser}
            inputValue={userSearch}
            loading={isSearchingUsers}
            filterOptions={(items) => items}
            getOptionLabel={(option) => option?.username ? `${option.username} — ${option.name}` : option?.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={(_, value, reason) => {
              setUserSearch(value)
              if (reason === 'input' && selectedUser) {
                setSelectedUser(null)
                setFilter('user_id', '')
              }
            }}
            onChange={(_, value) => {
              setSelectedUser(value)
              setFilter('user_id', value?.id || '')
              setUserSearch(value ? (value.username ? `${value.username} — ${value.name}` : value.name) : '')
            }}
            noOptionsText={userSearch.trim().length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhum usuário encontrado'}
            loadingText="Buscando usuários..."
            sx={{ minWidth: 240 }}
            renderInput={(params) => <TextField {...params} label="Usuário" size="small" placeholder="Digite nome ou usuário" />}
          />
          {selectFilter('model_type', 'Tipo de registro', options.types, '0', '1', (type) => displayTable({ model_type: type }))}
          {selectFilter('action', 'Ação', ACTIONS, '0', '1')}
          <TextField size="small" label="Dados" placeholder="ID, contrato, tombo, descrição..." value={filters.data} onChange={(event) => setFilter('data', event.target.value)} sx={{ minWidth: 220 }} />
          <TextField size="small" label="Estado" placeholder="Buscar nos dados" value={filters.state} onChange={(event) => setFilter('state', event.target.value)} />
          <TextField size="small" label="Cidade" placeholder="Buscar nos dados" value={filters.city} onChange={(event) => setFilter('city', event.target.value)} />
          <Button type="submit" variant="contained">Filtrar</Button>
          <Button type="button" onClick={() => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setSelectedUser(null); setUserSearch(''); setDebouncedUserSearch(''); setPage(1) }}>Limpar</Button>
        </Box>
      </Paper>
      {isError && <Alert severity="error">Não foi possível carregar o histórico de logs.</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>{['ID', 'Data/Hora', 'Aplicação', 'Órgão', 'Usuário', 'Ação', 'Operação'].map((title) => <TableCell key={title}>{title}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={7}>Carregando...</TableCell></TableRow>}
            {!isLoading && !logs.length && <TableRow><TableCell colSpan={7}>Nenhum log encontrado.</TableCell></TableRow>}
            {logs.map((log) => {
              return <Fragment key={log.id}>
                <TableRow hover>
                  <TableCell>{log.id}</TableCell><TableCell>{displayDateTime(log.created_at)}</TableCell>
                  <TableCell>{displayApplication(log)}</TableCell><TableCell>{log.organization?.name || '—'}</TableCell>
                  <TableCell>{log.user?.username || log.user?.name || `Usuário ${log.user_id || 'sistema'}`}</TableCell>
                  <TableCell>{displayTable(log)}</TableCell>
                  <TableCell><Button size="small" variant="outlined" onClick={() => setSelectedLog(log)}>{log.action.toUpperCase()}</Button></TableCell>
                </TableRow>
              </Fragment>
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, p: 2 }}>
        <Typography variant="body2">Página {data?.logs?.current_page || page} de {data?.logs?.last_page || 1} · {data?.logs?.total || 0} registros</Typography>
        <Button disabled={page <= 1 || isLoading} onClick={() => setPage(page - 1)}>Primeiro</Button>
        <Button disabled={page <= 1 || isLoading} onClick={() => setPage(page - 1)}>Voltar</Button>
        <Button disabled={page >= (data?.logs?.last_page || 1) || isLoading} onClick={() => setPage(page + 1)}>Próximo</Button>
        <Button disabled={page >= (data?.logs?.last_page || 1) || isLoading} onClick={() => setPage(data?.logs?.last_page || 1)}>Último</Button>
      </Box>
      <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} fullWidth maxWidth="lg">
        {selectedLog && <>
          <DialogTitle>Detalhes da alteração</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1, mb: 2 }}>
              <Typography><b>ID:</b> {selectedLog.id}</Typography>
              <Typography><b>Data/Hora:</b> {displayDateTime(selectedLog.created_at)}</Typography>
              <Typography><b>Aplicação:</b> {displayApplication(selectedLog)}</Typography>
              <Typography><b>Órgão:</b> {selectedLog.organization?.name || '—'}</Typography>
              <Typography><b>Usuário:</b> {selectedLog.user?.username || selectedLog.user?.name || 'Sistema'}</Typography>
              <Typography><b>Operação:</b> {selectedLog.action.toUpperCase()}</Typography>
            </Box>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f7f8fa' }}>
              <Typography><b>Dados:</b> Tabela {displayTable(selectedLog)}</Typography>
              <Typography><b>Modificado em:</b> id = {selectedLog.model_id}</Typography>
            </Paper>
            {selectedLog.action === 'update' && <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Dados Modificados</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small"><TableHead><TableRow><TableCell>Campo alterado</TableCell><TableCell>Valor antigo</TableCell><TableCell>Valor novo</TableCell></TableRow></TableHead>
                  <TableBody>{selectedChanges.length ? selectedChanges.map(({ key, fieldLabel: label, before, after, beforeLabel, afterLabel }) => <TableRow key={key}>
                    <TableCell>{label}</TableCell><TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{displayFieldValue(before, beforeLabel)}</TableCell><TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{displayFieldValue(after, afterLabel)}</TableCell>
                  </TableRow>) : <TableRow><TableCell colSpan={3}>Nenhuma diferença entre os snapshots disponíveis.</TableCell></TableRow>}</TableBody>
                </Table>
              </TableContainer>
            </Box>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {[['Dados Antigos', selectedLog.before, beforeLabelSnapshot(selectedLog)], ['Dados Novos', afterSnapshot(selectedLog), afterLabelSnapshot(selectedLog)]].map(([title, value, labels]) => <Box key={title}>
                <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', maxHeight: '55vh', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{snapshotText(value, labels)}</pre>
                </Paper>
              </Box>)}
            </Box>
          </DialogContent>
          <DialogActions><Button onClick={() => printChange(selectedLog, selectedChanges)} variant="contained">Imprimir relatório</Button><Button onClick={() => setSelectedLog(null)}>Fechar</Button></DialogActions>
        </>}
      </Dialog>
    </Box>
  </MainLayout>
}
